import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Form, Formik, FormikProps, useFormikContext } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { parseApiError } from 'shared/utils/apiValidationErrors';
import {
  Building2,
  Calculator,
  FileText,
  Handshake,
  Percent,
  Shield,
  ShieldHalf,
} from 'lucide-react';
import {
  BorneDto,
  GarantieReferentielDto,
  GarantieTunnelDetailDto,
  ModeCalculTarification,
  TypeBorne,
  UnitEnum,
} from 'api/funnel';
import { funnelReferentialsAPI, funnelsAPI } from 'services/swagger-api';
import { useDialog } from 'hooks/useDialog';
import { FormikDropdown } from 'components/forms/formik/FormikDropdown';
import { FormikInputText } from 'components/forms/formik/FormikInputText';
import { FormikSwitch } from 'components/forms/formik/FormikSwitch';
import { useAsyncResource } from 'back-office/shared/hooks/useAsyncResource';
import { useCompagnieOptions } from '../hooks/useActorOptions';
import { ExpressionVariable } from '../components/expression/ExpressionBuilder';
import { ModalShell, ModalSection } from 'back-office/shared/components/ModalShell';
import { RichTextField } from '../components/RichTextField';
import {
  CoInsurerFields,
  CoInsurerValues,
  PrincipalInsurerFields,
  ReInsurerFields,
  ReInsurerValues,
  RiskSplitSummary,
  totalQuotePart,
} from '../components/InsurerFields';
import {
  TarifFields,
  emptyTarifValues,
  extractTarifValues,
  prefixTarifValues,
  tarifFromFormValues,
  tarifToFormValues,
} from '../components/TarifFields';
import { BorneList } from '../components/BorneList';

/**
 * The back-end requires a duration (value + unit) on every borne: it carries the entered
 * default value and the chosen unit (amount by default for a new borne).
 */
const withDuree = (borne: BorneDto): BorneDto => ({
  ...borne,
  duree: {
    ...borne.duree,
    value: borne.valeurDefaut ?? borne.duree?.value ?? 0,
    unite: borne.duree?.unite ?? UnitEnum.Amount,
  },
});

interface GuaranteeFormValues extends Record<string, unknown> {
  garantieId: string;
  designation: string;
  descriptionCourte: string;
  descriptionLongue: string;
  /** Textes pour les documents (HTML, jetons {{CODE}}) ; facultatifs. */
  franchiseDetail: string;
  plafondDetail: string;
  isRequired: boolean;
  ordreAffichage: number | '';
  tarifModeCalcul: ModeCalculTarification;
  tarifMontant: number | '';
  tarifExpression: string;
  assureurPersonneMoraleId: string;
  compteBancaireId: string;
  tauxQP: number | '';
  aperition: boolean;
  coAssureurs: CoInsurerValues[];
  reAssureurs: ReInsurerValues[];
  franchises: BorneDto[];
  plafonds: BorneDto[];
  hasTaxeProfessionelle: boolean;
  taxeProModeCalcul: ModeCalculTarification;
  taxeProMontant: number | '';
  taxeProExpression: string;
  hasTaxeParticulier: boolean;
  taxePartModeCalcul: ModeCalculTarification;
  taxePartMontant: number | '';
  taxePartExpression: string;
}

/**
 * Le contenu de l'éditeur riche est du HTML : `<p><br></p>` s'affiche vide mais n'est
 * pas une chaîne vide. On ne juge donc que le texte, balises retirées.
 */
const hasRichText = (html?: string): boolean =>
  Boolean(
    html &&
      html
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/g, ' ')
        .trim()
  );

/**
 * A deductible or ceiling must be usable by the broker: a default value inside the
 * slider bounds, or one of the proposed values in list mode.
 */
const borneSchema = Yup.object().shape({
  designation: Yup.string().trim().required('La désignation est requise'),
  valeurMaximale: Yup.number()
    .nullable()
    .test('max-apres-min', 'Le maximum doit être supérieur au minimum', function (value) {
      const { type, valeurMinimale } = this.parent as BorneDto;
      if (type === TypeBorne.List) return true;
      return value != null && value > (valeurMinimale ?? 0);
    }),
  valeurs: Yup.array()
    .of(Yup.number())
    .test('liste-non-vide', 'Proposer au moins une valeur', function (value) {
      const { type } = this.parent as BorneDto;
      return type !== TypeBorne.List || (value?.length ?? 0) > 0;
    }),
  valeurDefaut: Yup.number()
    .nullable()
    .required('La valeur par défaut est requise')
    .test('defaut-valide', 'La valeur par défaut doit être entre le minimum et le maximum', function (value) {
      const { type, valeurMinimale, valeurMaximale, valeurs } = this.parent as BorneDto;
      if (value == null) return true;
      if (type === TypeBorne.List) {
        return (valeurs ?? []).includes(value)
          ? true
          : this.createError({ message: 'La valeur par défaut doit être une des valeurs proposées' });
      }
      return value >= (valeurMinimale ?? 0) && value <= (valeurMaximale ?? 0);
    }),
});

const validationSchema = Yup.object().shape({
  garantieId: Yup.string().required('La garantie du référentiel est requise'),
  franchises: Yup.array().of(borneSchema),
  plafonds: Yup.array().of(borneSchema),
  designation: Yup.string().trim().required('La désignation est requise'),
  descriptionCourte: Yup.string()
    .trim()
    .required('La description courte est requise'),
  descriptionLongue: Yup.string().test(
    'conditions-non-vides',
    "Les conditions d'application sont requises",
    (value) => hasRichText(value)
  ),
  assureurPersonneMoraleId: Yup.string().required("L'assureur principal est requis"),
  compteBancaireId: Yup.string().required('Le compte bancaire est requis'),
  tauxQP: Yup.number()
    .transform((value, original) => (original === '' ? undefined : value))
    // Le back stocke un entier.
    .integer('La quote-part est un nombre entier')
    .min(0, 'La quote-part ne peut pas être négative')
    .max(100, 'La quote-part ne peut pas dépasser 100 %')
    .nullable()
    // Porté par la quote-part de l'apériteur, seul champ toujours présent : le message
    // s'affiche donc même quand aucun co-assureur n'a encore été ajouté.
    .test(
      'quote-part-totale',
      'Le total des quotes-parts doit faire exactement 100 %',
      function () {
        const { tauxQP, coAssureurs, reAssureurs } = this.parent as GuaranteeFormValues;
        return totalQuotePart({ tauxQP, coAssureurs, reAssureurs }) === 100;
      }
    ),
});

const buildInitialValues = (guarantee: GarantieTunnelDetailDto | null): GuaranteeFormValues =>
  ({
    garantieId: guarantee?.garantieId ?? '',
    designation: guarantee?.designation ?? '',
    descriptionCourte: guarantee?.descriptionCourte ?? '',
    descriptionLongue: guarantee?.descriptionLongue ?? '',
    franchiseDetail: guarantee?.franchiseDetail ?? '',
    plafondDetail: guarantee?.plafondDetail ?? '',
    isRequired: guarantee?.isRequired ?? false,
    ordreAffichage: guarantee?.ordreAffichage ?? '',
    ...prefixTarifValues('tarif', tarifToFormValues(guarantee?.tarification)),
    assureurPersonneMoraleId: guarantee?.assureurPrincipal?.personneMoraleId ?? '',
    compteBancaireId: guarantee?.assureurPrincipal?.compteBancaire?.id ?? '',
    tauxQP: guarantee?.assureurPrincipal?.tauxQP ?? '',
    aperition: guarantee?.assureurPrincipal?.aperition ?? false,
    coAssureurs: (guarantee?.coAssureurs ?? []).map((coInsurer) => ({
      coAssureurPersonneMoraleId: coInsurer.personneMoraleId ?? '',
      compteBancaireId: coInsurer.compteBancaire?.id ?? '',
      tauxQP: coInsurer.tauxQP ?? '',
      aperition: coInsurer.aperition ?? false,
    })),
    reAssureurs: (guarantee?.reAssureurs ?? []).map((reInsurer) => ({
      reAssureurPersonneMoraleId: reInsurer.personneMoraleId ?? '',
      compteBancaireId: reInsurer.compteBancaire?.id ?? '',
      tauxQP: reInsurer.tauxQP ?? '',
      xS: reInsurer.xS ?? false,
      porteeXS: reInsurer.porteeXS ?? '',
      attachementXS: reInsurer.attachementXS ?? '',
      qP: reInsurer.qP ?? false,
    })),
    franchises: guarantee?.franchises ?? [],
    plafonds: guarantee?.plafonds ?? [],
    hasTaxeProfessionelle: Boolean(guarantee?.taxeProfessionelle),
    ...prefixTarifValues(
      'taxePro',
      guarantee?.taxeProfessionelle
        ? tarifToFormValues(guarantee.taxeProfessionelle)
        : emptyTarifValues()
    ),
    hasTaxeParticulier: Boolean(guarantee?.taxeParticulier),
    ...prefixTarifValues(
      'taxePart',
      guarantee?.taxeParticulier
        ? tarifToFormValues(guarantee.taxeParticulier)
        : emptyTarifValues()
    ),
  }) as GuaranteeFormValues;

interface GuaranteeModalProps {
  tunnelId: string;
  /** Absent en création. */
  garantieTunnelId?: string;
  title: string;
  /** Garanties déjà rattachées au tunnel, écartées du sélecteur en création. */
  attachedGuaranteeIds: string[];
  variables: ExpressionVariable[];
  onSuccess: () => void;
}

/**
 * Modale de création / modification d'une garantie du tunnel.
 *
 * Reprend l'enchaînement de sections de l'ancienne modale — identification, prime pure,
 * franchises, plafonds, assureurs — mais un seul composant couvre l'ajout et la
 * modification, là où l'ancien back-office maintenait deux modales de ~750 lignes de
 * template chacune, qui avaient divergé.
 */
export const GuaranteeModal: FC<GuaranteeModalProps> = ({
  tunnelId,
  garantieTunnelId,
  title,
  attachedGuaranteeIds,
  variables,
  onSuccess,
}) => {
  const formikRef = useRef<FormikProps<GuaranteeFormValues>>(null);
  const { closeDialog } = useDialog();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);

  const isEditMode = Boolean(garantieTunnelId);

  const fetchGuarantee = useCallback(
    () => funnelsAPI.getFunnelGuarantee({ garantieTunnelId: garantieTunnelId as string }),
    [garantieTunnelId]
  );
  const guarantee = useAsyncResource(fetchGuarantee, isEditMode);

  // Le socle de garanties dépend de la branche du tunnel ; le back le résout à partir de
  // l'identifiant du tunnel, le front n'a pas à connaître la branche.
  const fetchReferential = useCallback(
    () => funnelReferentialsAPI.getReferentialGuaranteesForFunnel({ tunnelId }),
    [tunnelId]
  );
  const referential = useAsyncResource(fetchReferential);

  const insurers = useCompagnieOptions();

  const initialValues = useMemo(() => buildInitialValues(guarantee.data), [guarantee.data]);

  // En création, une garantie déjà rattachée au tunnel n'est pas reproposée : le back
  // refuse le doublon. En modification, la garantie courante reste visible.
  const referentialItems = useMemo(
    () =>
      (referential.data ?? [])
        .filter(
          (item: GarantieReferentielDto) =>
            !attachedGuaranteeIds.includes(item.id) || item.id === guarantee.data?.garantieId
        )
        .map((item: GarantieReferentielDto) => ({
          key: item.id,
          label: item.shortLabel || item.label || item.code || 'Sans libellé',
          trailingLabel: item.code ?? undefined,
        }))
        .sort((a, b) => a.label.localeCompare(b.label, 'fr')),
    [referential.data, attachedGuaranteeIds, guarantee.data]
  );

  const handleSubmit = async (values: GuaranteeFormValues) => {
    setIsSubmitting(true);
    try {
      const command = {
        tunnelId,
        garantieId: values.garantieId,
        isRequired: values.isRequired,
        designation: values.designation.trim(),
        descriptionCourte: values.descriptionCourte.trim() || null,
        descriptionLongue: values.descriptionLongue.trim() || null,
        franchiseDetail: hasRichText(values.franchiseDetail) ? values.franchiseDetail.trim() : null,
        plafondDetail: hasRichText(values.plafondDetail) ? values.plafondDetail.trim() : null,
        ordreAffichage: values.ordreAffichage === '' ? 0 : Number(values.ordreAffichage),
        tarification: tarifFromFormValues(extractTarifValues('tarif', values)),
        assureurPersonneMoraleId: values.assureurPersonneMoraleId,
        compteBancaireId: values.compteBancaireId,
        tauxQP: values.tauxQP === '' ? 0 : Number(values.tauxQP),
        aperition: values.aperition,
        franchises: values.franchises.map(withDuree),
        plafonds: values.plafonds.map(withDuree),
        // Un bloc vide vaut suppression, l'omission vaudrait « ne pas toucher ». L'écran
        // affichant l'état réel des deux taxes, il énonce toujours l'intention : décocher
        // retire la taxe au lieu de la laisser en place à l'insu de l'utilisateur.
        taxeProfessionelle: values.hasTaxeProfessionelle
          ? tarifFromFormValues(extractTarifValues('taxePro', values))
          : {},
        taxeParticulier: values.hasTaxeParticulier
          ? tarifFromFormValues(extractTarifValues('taxePart', values))
          : {},
        coAssureurs: values.coAssureurs
          .filter((coInsurer) => coInsurer.coAssureurPersonneMoraleId)
          .map((coInsurer) => ({
            coAssureurPersonneMoraleId: coInsurer.coAssureurPersonneMoraleId,
            compteBancaireId: coInsurer.compteBancaireId || null,
            tauxQP: coInsurer.tauxQP === '' ? 0 : Number(coInsurer.tauxQP),
            aperition: coInsurer.aperition,
          })),
        reAssureurs: values.reAssureurs
          .filter((reInsurer) => reInsurer.reAssureurPersonneMoraleId)
          .map((reInsurer) => ({
            reAssureurPersonneMoraleId: reInsurer.reAssureurPersonneMoraleId,
            compteBancaireId: reInsurer.compteBancaireId || null,
            tauxQP: reInsurer.tauxQP === '' ? 0 : Number(reInsurer.tauxQP),
            xS: reInsurer.xS,
            porteeXS: reInsurer.porteeXS === '' ? null : Number(reInsurer.porteeXS),
            attachementXS:
              reInsurer.attachementXS === '' ? null : Number(reInsurer.attachementXS),
            qP: reInsurer.qP,
          })),
      };

      // Ces deux routes renvoient la garantie enregistrée, pas une enveloppe
      // d'opération : une promesse résolue vaut succès, un échec lève.
      if (isEditMode) {
        await funnelsAPI.updateFunnelGuarantee({
          updateGarantieTunnelCommand: {
            ...command,
            garantieTunnelId: garantieTunnelId as string,
          },
        });
      } else {
        await funnelsAPI.createFunnelGuarantee({ createGarantieTunnelCommand: command });
      }

      toast.success(isEditMode ? 'Garantie modifiée' : 'Garantie ajoutée');
      onSuccess();
      closeDialog();
    } catch (error) {
      console.error('Enregistrement de la garantie impossible', error);
      // Le back explique la formule refusée ou la variable inconnue : on relaie son message.
      const parsed = await parseApiError(error);
      toast.error(parsed.message || "L'enregistrement a échoué");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Formik
      innerRef={formikRef}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
      validateOnMount
    >
      <ModalShell
        title={title}
        subtitle={isEditMode ? 'Modification de la garantie' : 'Nouvelle garantie du tunnel'}
        isLoading={guarantee.isLoading || referential.isLoading}
        submit={{
          label: 'Sauvegarder',
          onClick: () => formikRef.current?.submitForm(),
          disabled: isSubmitting || !isFormValid,
        }}
      >
        <Form>
          <ValidityReporter onChange={setIsFormValid} />
          <ModalSection
            title="Identification"
            icon={<FileText className="h-4 w-4" />}
            subtitle="Ce que voit le souscripteur"
          >
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <FormikDropdown
                name="garantieId"
                label="Garantie du référentiel"
                required
                items={referentialItems}
                disabled={isEditMode}
                placeholder="Sélectionner une garantie produit"
              />
              <FormikInputText name="designation" label="Désignation" required />
              <FormikInputText
                name="descriptionCourte"
                label="Description courte"
                required
                placeholder="Texte affiché sur l'écran des garanties"
              />
              <FormikInputText
                name="ordreAffichage"
                label="Ordre d'affichage"
                type="number"
                placeholder="1"
              />
            </div>

            <div className="mt-4">
              <FormikSwitch name="isRequired" label="Garantie requise" />
              <p className="mt-0.5 text-xs text-gray-400">
                Indique si la garantie est imposée ou laissée au choix du souscripteur.
              </p>
            </div>

            <div className="mt-4">
              <DescriptionLongueField />
            </div>
          </ModalSection>

          <DetailsDocumentsSection variables={variables} />

          <ModalSection
            title="Prime pure HT"
            icon={<Calculator className="h-4 w-4" />}
            subtitle="Base de calcul du tarif"
          >
            <TarifFields prefix="tarif" label="Mode de calcul" required variables={variables} />
          </ModalSection>

          <ModalSection
            title="Taxes"
            icon={<Percent className="h-4 w-4" />}
            subtitle="Selon la qualité du souscripteur"
          >
            <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
              <TaxeField
                prefix="taxePro"
                switchName="hasTaxeProfessionelle"
                label="Taxe professionnelle"
              />
              <TaxeField
                prefix="taxePart"
                switchName="hasTaxeParticulier"
                label="Taxe particulier"
              />
            </div>
          </ModalSection>

          <ModalSection title="Franchises" icon={<Shield className="h-4 w-4" />} subtitle="Optionnel">
            <BorneList name="franchises" addLabel="Ajouter une franchise" />
          </ModalSection>

          <ModalSection title="Plafonds" icon={<Shield className="h-4 w-4" />} subtitle="Optionnel">
            <BorneList name="plafonds" addLabel="Ajouter un plafond" />
          </ModalSection>

          <ModalSection title="Assureur apériteur" icon={<Building2 className="h-4 w-4" />}>
            <PrincipalInsurerFields
              insurers={insurers.data ?? []}
              isLoading={insurers.isLoading}
            />
          </ModalSection>

          <ModalSection
            title="Co-assureurs"
            icon={<Handshake className="h-4 w-4" />}
            subtitle="Optionnel"
          >
            <CoInsurerFields insurers={insurers.data ?? []} />
          </ModalSection>

          <ModalSection
            title="Ré-assureurs"
            icon={<ShieldHalf className="h-4 w-4" />}
            subtitle="Optionnel"
          >
            <ReInsurerFields insurers={insurers.data ?? []} />
          </ModalSection>

          <RiskSplitSummary />
        </Form>
      </ModalShell>
    </Formik>
  );
};

/**
 * Remonte la validité du formulaire au bouton de la coquille, qui vit hors du <Form>.
 *
 * `validateOnMount` rend la validité exacte dès l'ouverture : une garantie incomplète
 * n'offre pas un bouton actif qui échouerait au clic.
 */
const ValidityReporter: FC<{ onChange: (isValid: boolean) => void }> = ({ onChange }) => {
  const { isValid } = useFormikContext<GuaranteeFormValues>();

  useEffect(() => {
    onChange(isValid);
  }, [isValid, onChange]);

  return null;
};

/**
 * Une des deux taxes de la garantie.
 *
 * Le commutateur porte l'intention : activé, la taxe est créée ou remplacée ; désactivé,
 * elle est explicitement retirée. Le taux est un multiplicateur du prix HT, pas un
 * pourcentage — le champ le rappelle.
 */
const TaxeField: FC<{ prefix: string; switchName: string; label: string }> = ({
  prefix,
  switchName,
  label,
}) => {
  const { values } = useFormikContext<Record<string, unknown>>();
  const isEnabled = Boolean(values[switchName]);

  return (
    <div className="rounded-lg border border-gray-100 bg-gray-50/60 p-4">
      <FormikSwitch name={switchName} label={label} />
      {isEnabled ? (
        <div className="mt-4">
          <TarifFields
            prefix={prefix}
            label="Mode de calcul"
            allowExpression={false}
            rateAsMultiplier
          />
        </div>
      ) : (
        <p className="mt-2 text-xs text-gray-400">
          Aucune taxe appliquée. L'enregistrement retirera celle éventuellement en place.
        </p>
      )}
    </div>
  );
};

/** Conditions d'application de la garantie, stockées en HTML par le back. */
/**
 * Textes pour les documents : facultatifs et propres à certains produits, la section reste
 * repliée sauf si un texte existe déjà. Chaque éditeur propose les variables du tunnel en
 * tapant « {{ » ; le back refuse un jeton qui n'en désigne aucune.
 */
const DetailsDocumentsSection: FC<{ variables: ExpressionVariable[] }> = ({ variables }) => {
  const { values, setFieldValue } = useFormikContext<GuaranteeFormValues>();
  const hasContent = hasRichText(values.franchiseDetail) || hasRichText(values.plafondDetail);

  return (
    <ModalSection
      title="Textes pour les documents"
      icon={<FileText className="h-4 w-4" />}
      subtitle={hasContent ? 'Renseignés' : 'Optionnel'}
      collapsible
      defaultOpen={hasContent}
    >
      <p className="mb-3 text-xs text-gray-400">
        Colonnes du tableau des garanties des documents (devis, proposition, attestation,
        conditions particulières). Tapez <code>{'{{'}</code> pour insérer une variable du
        tunnel, remplacée par sa valeur pour le dossier. Vide = texte par défaut du produit.
      </p>
      <div className="space-y-4">
        <RichTextField
          name="franchiseDetail"
          label="Franchise"
          value={values.franchiseDetail}
          onChange={(html) => setFieldValue('franchiseDetail', html)}
          variables={variables}
        />
        <RichTextField
          name="plafondDetail"
          label="Plafond"
          value={values.plafondDetail}
          onChange={(html) => setFieldValue('plafondDetail', html)}
          variables={variables}
        />
      </div>
    </ModalSection>
  );
};

const DescriptionLongueField: FC = () => {
  const { values, setFieldValue, errors, touched } =
    useFormikContext<GuaranteeFormValues>();

  return (
    <RichTextField
      name="descriptionLongue"
      label="Conditions d'application"
      required
      value={values.descriptionLongue}
      onChange={(html) => setFieldValue('descriptionLongue', html)}
      error={touched.descriptionLongue ? (errors.descriptionLongue as string) : undefined}
      hint="Texte affiché sur l'écran d'aide dédié à l'explication de la garantie."
    />
  );
};
